import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeletePreRegistrationConfig,
  useDeleteTournamentResource,
  useGetPreRegistrationConfig,
  useGetTournamentResourceUrl,
  useSavePreRegistrationConfig,
  useUploadTournamentResource,
} from "../hooks/tournamentHooks";
import { useNotification } from '@iziping/ui';
import Toggle from "../../../components/shared/Toggle";
import ConfirmationModal from "../../../components/shared/ConfirmationModal";
import FileUploadInput from "../../../components/shared/FileUploadInput";
import Modal from "../../../components/modal/Modal";
import { Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import styles from "../../../styles/form.module.css";
import { useTournamentStore } from '../../../service/useTournamentStore';

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_IMAGE_LABELS = ["JPG", "PNG", "WEBP"];
const ACCEPTED_PDF_TYPES = ["application/pdf"];
const ACCEPTED_PDF_LABELS = ["PDF"];

const TournamentPreRegistrationConfig: React.FC = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { selectedTournament } = useTournamentStore();
  const tournamentId = selectedTournament?.id ?? 0;

  const { data: savedData, isLoading } = useGetPreRegistrationConfig(tournamentId);
  const save = useSavePreRegistrationConfig(tournamentId);
  const deleteConfig = useDeletePreRegistrationConfig(tournamentId);
  const uploadResource = useUploadTournamentResource(tournamentId);
  const deleteResource = useDeleteTournamentResource(tournamentId);
  const getResourceUrl = useGetTournamentResourceUrl(tournamentId);

  const [enabled, setEnabled] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [posterUrl, setPosterUrl] = useState<string | undefined>();
  const [rulesUrl, setRulesUrl] = useState<string | undefined>();
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [previewType, setPreviewType] = useState<"image" | "pdf">("image");

  useEffect(() => {
    if (savedData) {
      setEnabled(savedData.preRegistrationEnabled);
      if (savedData.preRegistrationConfig) {
        setShowStats(savedData.preRegistrationConfig.showStats);
        setPosterUrl(savedData.preRegistrationConfig.posterUrl);
        setRulesUrl(savedData.preRegistrationConfig.rulesUrl);
      }
    }
  }, [savedData]);

  const registrationLink = savedData?.preRegistrationConfig?.registrationId
    ? `${window.location.origin}/registration/${savedData.preRegistrationConfig.registrationId}`
    : "";

  const handleToggle = (value: boolean) => {
    if (!value && enabled) {
      setShowDisableModal(true);
    } else {
      setEnabled(value);
    }
  };

  const handleConfirmDisable = async () => {
    try {
      await deleteConfig.mutateAsync();
      setEnabled(false);
      setShowStats(false);
      setPosterUrl(undefined);
      setRulesUrl(undefined);
      notify(t("tournament.preRegistrationConfig.delete.success"), "success");
    } catch (error: any) {
      notify(t(error.error) || t("internal.server.error"), "error");
    } finally {
      setShowDisableModal(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync({ showStats });
      notify(t("tournament.preRegistrationConfig.save.success"), "success");
    } catch (error: any) {
      notify(t(error.error) || t("internal.server.error"), "error");
    }
  };

  const handleRemovePoster = async () => {
    try {
      await deleteResource.mutateAsync({ type: "POSTER" });
      setPosterUrl(undefined);
    } catch (error: any) {
      notify(t(error.error) || t("internal.server.error"), "error");
    }
  };

  const handleRemoveRules = async () => {
    try {
      await deleteResource.mutateAsync({ type: "RULES" });
      setRulesUrl(undefined);
    } catch (error: any) {
      notify(t(error.error) || t("internal.server.error"), "error");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(registrationLink);
    notify(t("tournament.preRegistrationConfig.link.copied"), "success");
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div
      style={{
        maxWidth: "560px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <Toggle
        checked={enabled}
        onChange={handleToggle}
        label={enabled ? t("tournament.preRegistrationConfig.enabled") : t("tournament.preRegistrationConfig.disabled")}
      />

      <ConfirmationModal
        isOpen={showDisableModal}
        title={t("tournament.preRegistrationConfig.disable.confirm.title")}
        message={t("tournament.preRegistrationConfig.disable.confirm.message")}
        confirmLabel={t("tournament.preRegistrationConfig.disable.confirm.button")}
        onConfirm={handleConfirmDisable}
        onCancel={() => setShowDisableModal(false)}
      />

      {enabled && (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t("tournament.preRegistrationConfig.link.label")}</label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                className={styles.input}
                readOnly
                value={registrationLink}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                style={{ flex: 1, cursor: "default" }}
              />
              <button
                type="button"
                className="button"
                onClick={handleCopy}
                style={{ padding: "8px 12px", flexShrink: 0 }}
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
          {registrationLink && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <span className={styles.label}>{t("tournament.preRegistrationConfig.qrcode")}</span>
              <QRCodeSVG value={registrationLink} size={160} />
            </div>
          )}
          <FileUploadInput
            label={t("tournament.preRegistrationConfig.poster")}
            acceptedMimeTypes={ACCEPTED_IMAGE_TYPES}
            acceptedFormatsLabel={ACCEPTED_IMAGE_LABELS}
            maxSizeMb={2}
            currentUrl={posterUrl}
            uploadFn={async (file) => {
              const result = await uploadResource.mutateAsync({
                type: "POSTER",
                file,
              });
              return result.url;
            }}
            onUpload={setPosterUrl}
            onRemove={handleRemovePoster}
            onError={(err: any) => notify(t(err?.error) || t("internal.server.error"), "error")}
            onPreview={
              posterUrl
                ? async () => {
                    const { url } = await getResourceUrl.mutateAsync({
                      type: "POSTER",
                    });
                    setPreviewType("image");
                    setPreviewUrl(url);
                  }
                : undefined
            }
          />
          <FileUploadInput
            label={t("tournament.preRegistrationConfig.rules")}
            acceptedMimeTypes={ACCEPTED_PDF_TYPES}
            acceptedFormatsLabel={ACCEPTED_PDF_LABELS}
            maxSizeMb={5}
            currentUrl={rulesUrl}
            uploadFn={async (file) => {
              const result = await uploadResource.mutateAsync({
                type: "RULES",
                file,
              });
              return result.url;
            }}
            onUpload={setRulesUrl}
            onRemove={handleRemoveRules}
            onError={(err: any) => notify(t(err?.error) || t("internal.server.error"), "error")}
            onPreview={
              rulesUrl
                ? async () => {
                    const { url } = await getResourceUrl.mutateAsync({
                      type: "RULES",
                    });
                    setPreviewType("pdf");
                    setPreviewUrl(url);
                  }
                : undefined
            }
          />
          {previewUrl && (
            <Modal onClose={() => setPreviewUrl(undefined)}>
              {previewType === "image" ? (
                <img
                  src={previewUrl}
                  alt="preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    display: "block",
                  }}
                />
              ) : (
                <iframe src={previewUrl} title="preview" style={{ width: "70vw", height: "80vh", border: "none" }} />
              )}
            </Modal>
          )}{" "}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 0",
              borderTop: "1px solid #e0e8ed",
            }}
          >
            <span style={{ fontSize: "0.95rem", fontWeight: 500, color: "#444" }}>
              {t("tournament.preRegistrationConfig.showStatistics")}
            </span>
            <Toggle checked={showStats} onChange={setShowStats} />
          </div>
          <button type="submit" className="button" disabled={save.isPending}>
            {save.isPending ? "..." : t("tournament.preRegistrationConfig.save.button")}
          </button>
        </form>
      )}
    </div>
  );
};

export default TournamentPreRegistrationConfig;
